import React, { useState } from "react";
import { Button, ButtonGroup, Form } from "react-bootstrap";
import { enums } from "../../../data";
import { useDispatch, useSelector } from "react-redux";
import { setApplications } from "../../../state/slices/application";
import { useGetCoursesQuery } from "../../../state/services/course";

// This component renders a section where user can select a course and then click
// on the "rank" button to rank all the applications.
// When the rank button is clicked, a score is assigned to each applicant. The applicants
// are sorted based on this score. A score colum is added to the right side of the Ag-Grid
// to display this score.
// The score is calculated based on interest, experience, grade and GPA.
function RankApplicationComponent(props) {
  const { year, term, gridApi, gridColumnApi } = props;
  const [course, setCourse] = useState("");

  const applications = useSelector((state) => state.applications.records);
  const dispatch = useDispatch();

  const { data: courses } = useGetCoursesQuery({ year, term });

  const rankApplications = (event) => {
    event.preventDefault();

    // Update column definition
    const columnDefs = gridApi.getColumnDefs();
    if (columnDefs[columnDefs.length - 1].field !== "score") {
      const colDef = [
        ...columnDefs,
        {
          field: "score",
          headerName: "Score",
          pinned: "right",
          width: 110,
          filter: "agNumberColumnFilter",
          editable: false,
          comparator: (valueA, valueB, nodeA, nodeB, isInverted) =>
            Number(valueA) - Number(valueB),
        },
      ];
      // setColumnDefs(colDef);
      gridApi.setColumnDefs(colDef);
    }

    // calculate score
    const apps = [];
    for (let i = 0; i < applications.length; i++) {
      const app = applications[i];
      let score = 0;

      // here comes the logic!
      if (app.course1 === course) {
        score = 8;
        score += enums.courseExperience.indexOf(app.experience1);
        score +=
          (enums.grades.length - enums.grades.indexOf(app.grade1) - 1) / 2.0;
      } else if (app.course2 === course) {
        score = 6;
        score += enums.courseExperience.indexOf(app.experience2);
        score +=
          (enums.grades.length - enums.grades.indexOf(app.grade2) - 1) / 2.0;
      } else if (app.course3 === course) {
        score = 4;
        score += enums.courseExperience.indexOf(app.experience3);
        score +=
          (enums.grades.length - enums.grades.indexOf(app.grade3) - 1) / 2.0;
      } else if (app.course4 === course) {
        score = 2;
        score += enums.courseExperience.indexOf(app.experience4);
        score +=
          (enums.grades.length - enums.grades.indexOf(app.grade4) - 1) / 2.0;
      } else {
        score = 0;
      }

      score += app.gradePointAverage / 4.0;
      score += enums.teachingExperience.indexOf(app.experience) / 2.0;

      apps.push({ ...app, score: score.toFixed(2) });
    }

    dispatch(setApplications(apps));

    // clear sort
    gridColumnApi.applyColumnState({ defaultState: { sort: null } });
    // sort the score column
    gridColumnApi.applyColumnState({
      state: [
        {
          colId: "score",
          sort: "desc",
        },
      ],
      defaultState: { sort: null },
    });

    gridApi.redrawRows();
  };

  const clearRanks = (event) => {
    event.preventDefault();

    // Update column definition
    const columnDefs = gridApi.getColumnDefs();
    if (columnDefs[columnDefs.length - 1].field === "score") {
      const colDef = columnDefs;
      colDef.pop();
      gridApi.setColumnDefs(colDef);
    }

    dispatch(
      setApplications(
        applications.map((app) => {
          const { score, ...rest } = app;
          return rest;
        })
      )
    );

    // clear sort
    gridColumnApi.applyColumnState({ defaultState: { sort: null } });

    gridApi.redrawRows();
  };

  const sortByInterest = (event) => {
    event.preventDefault();

    // Calculate interest level for each application
    const apps = applications.map((app) => {
      let interestLevel = 5; // Default: didn't apply for this course
      if (app.course1 === course) {
        interestLevel = 1;
      } else if (app.course2 === course) {
        interestLevel = 2;
      } else if (app.course3 === course) {
        interestLevel = 3;
      } else if (app.course4 === course) {
        interestLevel = 4;
      }
      return { ...app, _interestLevel: interestLevel };
    });

    // Sort by interest level (ascending: 1 first, then 2, 3, 4, 5)
    // Stable sort preserves original order for same interest level
    apps.sort((a, b) => a._interestLevel - b._interestLevel);

    // Remove temporary field and update Redux
    dispatch(setApplications(apps.map(({ _interestLevel, ...rest }) => rest)));

    // Clear any existing column sorts
    gridColumnApi.applyColumnState({ defaultState: { sort: null } });
    gridApi.redrawRows();
  };

  const clearInterestSort = (event) => {
    event.preventDefault();
    // Reset any column sorting
    gridColumnApi.applyColumnState({ defaultState: { sort: null } });
    gridApi.redrawRows();
  };

  return (
    <Form size={"sm"}>
      {/* Course Selection - shared by both features */}
      <Form.Label size={"sm"}>Select your course</Form.Label>
      <Form.Control
        size={"sm"}
        name={"course"}
        as={"select"}
        type={"text"}
        onChange={(e) => setCourse(e.target.value)}
        value={course}
      >
        {courses &&
          courses.map((option, ind) => {
            return (
              <option key={ind} value={option}>
                {option}
              </option>
            );
          })}
      </Form.Control>

      {/* Section 1: Composite Score Ranking (existing feature) */}
      <p className="mt-3">
        <span>
          This is an <u>experimental</u> feature.
        </span>{" "}
        The applications are ranked based on interest, experience, grade and
        GPA. The ranking is applied to the entirety of applications ({term}{" "}
        {year}), not only the filtered view shown in the table below.
      </p>
      <ButtonGroup size={"sm"}>
        <Button
          className={"section-button"}
          variant={"outline-danger"}
          onClick={clearRanks}
        >
          Clear
        </Button>
        <Button
          className={"section-button"}
          type={"button"}
          onClick={rankApplications}
        >
          Rank and Sort
        </Button>
      </ButtonGroup>

      {/* Section 2: Interest-Based Sorting (NEW feature) */}
      <p className="mt-3">
        Sort applicants by their level of interest in the selected course.
        Applicants who chose this course as their 1st choice will appear first,
        followed by 2nd choice, 3rd choice, and 4th choice. Within the same
        interest level, applicants appear in the order they submitted their
        applications.
      </p>
      <ButtonGroup size={"sm"}>
        <Button
          className={"section-button"}
          variant={"outline-danger"}
          onClick={clearInterestSort}
        >
          Clear
        </Button>
        <Button
          className={"section-button"}
          type={"button"}
          onClick={sortByInterest}
        >
          Sort by Interest
        </Button>
      </ButtonGroup>
    </Form>
  );
}

export default RankApplicationComponent;
